import {
  FileJson,
  FileCode,
  FileText,
  FileLock2,
  Binary,
  Hash,
  QrCode,
  Clock,
  Palette,
  Image,
  Type,
  Link,
  Fingerprint,
  RefreshCw,
  Lock,
  Database,
  FileSpreadsheet,
  GitBranch,
  Code,
  Radio,
  FileImage,
  Regex,
  Table,
  Globe,
  Scissors,
  LayoutTemplate,
  Grid2x2,
  FileSearch,
  Calculator,
  KeyRound,
  Shield,
  Network,
  Code2,
  Braces,
  Wrench,
  File,
  CalendarRange,
  Globe2,
  TimerReset,
  Sparkles,
  Terminal,
  ShieldCheck,
  Share2,
  Zap,
  Wifi,
  Activity,
  Gamepad2,
  Layers,
  Monitor,
  Keyboard,
  MousePointer2,
  Hand,
  Volume2,
  Mic,
  Camera,
  Gauge,
  Compass,
  BatteryFull,
  Cpu,
  MemoryStick,
  MonitorCog,
  HardDrive,
} from 'lucide-react';

export interface ToolConfig {
  id: string;
  name: string;
  description: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  category: ToolCategory;
  keywords?: readonly string[];
  search: ToolSearchMetadata;
  containerWidth?: 'full' | 'wide' | 'standard' | 'narrow';
}

export interface ToolSearchMetadata {
  aliases?: readonly string[];
  actions: readonly string[];
  inputs?: readonly string[];
  outputs?: readonly string[];
  qualifiers?: readonly string[];
  tasks?: readonly string[];
}

export type ToolCategory =
  | 'ai'
  | 'converters'
  | 'encoders'
  | 'generators'
  | 'text'
  | 'image'
  | 'time'
  | 'color'
  | 'development'
  | 'data'
  | 'security'
  | 'network'
  | 'hardware';

export const TOOL_CATEGORIES: { id: ToolCategory; name: string }[] = [
  { id: 'ai', name: 'AI & Prompt Tools' },
  { id: 'converters', name: 'Format & Code Conversion' },
  { id: 'encoders', name: 'Encoding & Decoding' },
  { id: 'generators', name: 'Generators' },
  { id: 'text', name: 'Text & Content' },
  { id: 'image', name: 'Images & Files' },
  { id: 'time', name: 'Date & Time' },
  { id: 'color', name: 'Color & Design' },
  { id: 'development', name: 'Code & Developer Tools' },
  { id: 'data', name: 'Data & SQL' },
  { id: 'security', name: 'Security & Tokens' },
  { id: 'network', name: 'API & Network' },
  { id: 'hardware', name: 'Hardware & Device Tests' },
];

export const TOOLS: ToolConfig[] = [
  // Converters
  {
    id: 'format-converter',
    name: 'JSON, YAML, XML, CSV & TOML Converter',
    description: 'Convert between JSON, YAML, XML, CSV, and TOML formats',
    path: '/format-converter',
    icon: FileJson,
    category: 'converters',
    keywords: ['data format', 'transform', 'beautify', 'toml'],
    search: {
      aliases: ['data format converter', 'json yaml converter'],
      actions: ['convert', 'transform'],
      inputs: ['json', 'yaml', 'xml', 'csv', 'toml'],
      outputs: ['json', 'yaml', 'xml', 'csv', 'toml'],
      tasks: ['convert data formats', 'change json to yaml', 'csv to json'],
    },
  },
  {
    id: 'code-converter',
    name: 'JSON to Code Models',
    description:
      'Convert JSON objects to starter models in C#, TypeScript, Java, Python, Go, or Kotlin',
    path: '/code-converter',
    icon: FileCode,
    category: 'converters',
    keywords: ['class generator', 'model generator', 'quicktype'],
    search: {
      aliases: ['json to class', 'json model generator'],
      actions: ['convert', 'generate'],
      inputs: ['json'],
      outputs: ['typescript', 'c#', 'java', 'python', 'go', 'kotlin', 'model', 'interface'],
      tasks: ['turn json into typescript interface', 'generate class from json'],
    },
  },
  {
    id: 'number-base',
    name: 'Number Base Converter',
    description: 'Convert between Decimal, Binary, Octal, and Hexadecimal',
    path: '/number-base',
    icon: Binary,
    category: 'converters',
    keywords: ['decimal', 'octal', 'hexadecimal', 'binary'],
    search: {
      aliases: ['radix converter'],
      actions: ['convert'],
      inputs: ['number', 'decimal', 'binary', 'octal', 'hexadecimal'],
      outputs: ['decimal', 'binary', 'octal', 'hexadecimal'],
      tasks: ['convert binary to decimal', 'hex to decimal'],
    },
  },

  // Encoders
  {
    id: 'base64',
    name: 'Base64 Encoder & Decoder',
    description: 'Encode UTF-8 text as Base64 and decode Base64 to text',
    path: '/base64',
    icon: FileCode,
    category: 'encoders',
    keywords: ['base 64', 'atob', 'btoa'],
    search: {
      aliases: ['base 64 converter'],
      actions: ['encode', 'decode'],
      inputs: ['text', 'base64'],
      outputs: ['text', 'base64'],
      tasks: ['decode base64 string', 'encode text as base64'],
    },
  },
  {
    id: 'url',
    name: 'URL Encoder & Decoder',
    description: 'Percent-encode and decode individual URL values',
    path: '/url',
    icon: Link,
    category: 'encoders',
    keywords: ['uri', 'percent encoding', 'query string'],
    search: {
      aliases: ['uri encoder', 'percent encoder', 'url encoder decoder online', 'urlencode'],
      actions: ['encode', 'decode'],
      inputs: ['url', 'uri', 'query string'],
      outputs: ['url', 'uri'],
      tasks: ['encode url parameters', 'decode percent encoded url'],
    },
  },
  {
    id: 'hex',
    name: 'Text & Hex Converter',
    description: 'Convert text to Hex and vice versa',
    path: '/hex',
    icon: Binary,
    category: 'encoders',
    keywords: ['hexadecimal', 'encode', 'decode'],
    search: {
      aliases: ['text to hex'],
      actions: ['convert', 'encode', 'decode'],
      inputs: ['text', 'hex', 'hexadecimal'],
      outputs: ['text', 'hex', 'hexadecimal'],
      tasks: ['convert text to hex', 'decode hexadecimal text'],
    },
  },
  {
    id: 'binary',
    name: 'Text & Binary Converter',
    description: 'Convert text to Binary and vice versa',
    path: '/binary',
    icon: Binary,
    category: 'encoders',
    keywords: ['bits', 'encode', 'decode'],
    search: {
      aliases: ['text to binary', 'binary translator', 'bit translator', 'binary code converter'],
      actions: ['convert', 'encode', 'decode'],
      inputs: ['text', 'binary', 'bits'],
      outputs: ['text', 'binary'],
      tasks: ['convert text to binary', 'decode binary text'],
    },
  },

  // Generators
  {
    id: 'uuid',
    name: 'UUID Generator',
    description: 'Generate UUIDs (v1, v3, v4, v5)',
    path: '/uuid',
    icon: Fingerprint,
    category: 'generators',
    keywords: ['guid', 'unique id', 'random id'],
    search: {
      aliases: ['guid generator', 'unique id generator'],
      actions: ['generate', 'create'],
      outputs: ['uuid', 'guid', 'unique id'],
      qualifiers: ['random', 'v1', 'v4'],
      tasks: ['create a random uuid', 'generate guid'],
    },
  },
  {
    id: 'hash',
    name: 'Hash Generator',
    description: 'Generate text hashes and HMACs with MD5, SHA-1, SHA-256, SHA-512, and RIPEMD-160',
    path: '/hash',
    icon: Hash,
    category: 'generators',
    keywords: ['checksum', 'digest', 'sha', 'md5'],
    search: {
      aliases: ['checksum generator', 'digest generator'],
      actions: ['generate', 'hash'],
      inputs: ['text'],
      outputs: ['md5', 'sha1', 'sha256', 'sha512', 'ripemd160', 'hmac', 'hash', 'checksum'],
      tasks: ['calculate text hash', 'create hmac checksum'],
    },
  },
  {
    id: 'qrcode',
    name: 'QR Code Generator',
    description: 'Generate QR Codes',
    path: '/qrcode',
    icon: QrCode,
    category: 'generators',
    keywords: ['qr', 'link to qr', 'karekod', 'qr olustur'],
    search: {
      aliases: ['qr generator', 'karekod olusturucu'],
      actions: ['generate', 'create'],
      inputs: ['text', 'url'],
      outputs: ['qr code', 'karekod'],
      tasks: ['turn link into qr code', 'qr kod olustur'],
    },
  },
  {
    id: 'placeholder',
    name: 'Placeholder Text',
    description: 'Generate Lorem, Bacon, Hipster, and Corporate Ipsum',
    path: '/placeholder',
    icon: FileText,
    category: 'generators',
    search: {
      aliases: ['ipsum generator', 'lorem ipsum generator', 'dummy text generator'],
      actions: ['generate', 'create'],
      outputs: [
        'placeholder text',
        'lorem ipsum',
        'bacon ipsum',
        'hipster ipsum',
        'corporate ipsum',
      ],
      tasks: ['create placeholder copy', 'generate dummy content', 'generate lorem ipsum'],
    },
  },
  {
    id: 'mock-data',
    name: 'Mock Data Generator',
    description: 'Generate mock data (JSON, CSV, SQL) based on schema',
    path: '/mock-data',
    icon: Table,
    category: 'generators',
    keywords: ['fake data', 'sample data', 'test data', 'seed data'],
    search: {
      aliases: ['fake data generator', 'fixture generator'],
      actions: ['generate', 'create'],
      inputs: ['schema'],
      outputs: ['json', 'csv', 'sql', 'mock data'],
      tasks: ['generate test data from schema', 'create fake users'],
    },
  },
  {
    id: 'favicon-generator',
    name: 'Favicon Generator',
    description: 'Generate favicons and ICO files from images',
    path: '/favicon-generator',
    icon: LayoutTemplate,
    category: 'generators',
    search: {
      aliases: ['ico generator', 'website icon generator'],
      actions: ['generate', 'create', 'convert'],
      inputs: ['image', 'png', 'jpg'],
      outputs: ['favicon', 'ico'],
      tasks: ['make favicon from image', 'convert png to ico'],
    },
  },

  // Text
  {
    id: 'text-utils',
    name: 'Text Case & Cleanup',
    description: 'Convert text case and remove duplicate lines',
    path: '/text-utils',
    icon: Type,
    category: 'text',
    keywords: ['uppercase', 'lowercase', 'deduplicate', 'remove duplicate lines'],
    search: {
      aliases: ['text cleaner', 'case converter'],
      actions: ['clean', 'transform', 'deduplicate'],
      inputs: ['text'],
      outputs: ['uppercase', 'lowercase', 'clean text'],
      tasks: ['remove duplicate lines', 'change text to uppercase'],
    },
  },
  {
    id: 'slug',
    name: 'Slug Generator',
    description: 'Generate URL-friendly slugs',
    path: '/slug',
    icon: Link,
    category: 'text',
    keywords: ['seo url', 'permalink', 'url friendly text'],
    search: {
      aliases: ['url slug maker'],
      actions: ['generate', 'convert'],
      inputs: ['text', 'title'],
      outputs: ['slug', 'permalink'],
      tasks: ['make seo friendly url', 'turn title into slug'],
    },
  },
  {
    id: 'crypto',
    name: 'Text Encryption & Decryption',
    description: 'Encrypt text with AES-GCM or decrypt supported legacy ciphertext',
    path: '/crypto',
    icon: RefreshCw,
    category: 'text',
    keywords: ['aes', 'des', 'rc4', 'crypto'],
    search: {
      aliases: ['text cipher', 'aes tool', 'decrypt online', 'encryption decryption online'],
      actions: ['encrypt', 'decrypt'],
      inputs: ['text'],
      outputs: ['encrypted text', 'plain text'],
      qualifiers: ['aes-gcm', 'legacy aes', 'des', 'rc4'],
      tasks: ['encrypt text with aes-gcm', 'decrypt legacy ciphertext'],
    },
  },
  {
    id: 'markdown',
    name: 'Markdown Editor',
    description: 'Markdown editor with live preview and HTML export',
    path: '/markdown',
    icon: FileText,
    category: 'text',
    keywords: ['md', 'readme preview', 'markdown to html'],
    search: {
      aliases: ['md editor', 'readme editor'],
      actions: ['edit', 'preview', 'convert'],
      inputs: ['markdown', 'md', 'readme'],
      outputs: ['html', 'preview'],
      tasks: ['preview readme', 'convert markdown to html'],
    },
  },
  {
    id: 'ascii-art',
    name: 'ASCII Art Generator',
    description: 'Generate ASCII art from text',
    path: '/ascii-art',
    icon: Type,
    category: 'text',
    search: {
      aliases: ['text art generator'],
      actions: ['generate', 'create'],
      inputs: ['text'],
      outputs: ['ascii art'],
      tasks: ['turn text into ascii art', 'create terminal banner'],
    },
  },
  {
    id: 'diff-checker',
    name: 'Diff Checker',
    description: 'Compare and find differences between two texts',
    path: '/diff-checker',
    icon: FileSearch,
    category: 'text',
    keywords: ['compare text', 'compare files', 'text difference', 'iki metni karsilastir'],
    search: {
      aliases: ['text compare', 'file difference checker'],
      actions: ['compare', 'diff'],
      inputs: ['text', 'file'],
      outputs: ['difference', 'diff'],
      tasks: ['compare two texts'],
    },
  },
  {
    id: 'word-counter',
    name: 'Word Counter',
    description: 'Count words, characters, sentences, and paragraphs',
    path: '/word-counter',
    icon: Calculator,
    category: 'text',
    keywords: ['character count', 'letter count', 'kelime say', 'metin say'],
    search: {
      aliases: ['character counter', 'text statistics'],
      actions: ['count', 'calculate'],
      inputs: ['text'],
      outputs: ['word count', 'character count', 'sentence count'],
      tasks: ['count words in text', 'kelime ve karakter say'],
    },
  },
  {
    id: 'html-entities',
    name: 'HTML Entity Encoder & Decoder',
    description: 'Encode and decode HTML entities',
    path: '/html-entities',
    icon: Code,
    category: 'text',
    search: {
      aliases: ['html escape tool'],
      actions: ['encode', 'decode', 'escape', 'unescape'],
      inputs: ['html', 'html entity', 'text'],
      outputs: ['html entity', 'text'],
      tasks: ['encode html special characters', 'decode html entities'],
    },
  },
  {
    id: 'string-escaper',
    name: 'String Escape & Unescape',
    description: 'Escape and unescape strings for various formats',
    path: '/string-escaper',
    icon: Code2,
    category: 'text',
    search: {
      aliases: ['string encoder', 'escape characters tool'],
      actions: ['escape', 'unescape', 'encode', 'decode'],
      inputs: ['string', 'text'],
      outputs: ['escaped string', 'plain text'],
      tasks: ['escape quotes in string', 'unescape backslashes'],
    },
  },
  {
    id: 'regex-tester',
    name: 'Regex Tester & Pattern Library',
    description: 'Test JavaScript regular expressions with common starter patterns',
    path: '/regex-tester',
    icon: Regex,
    category: 'text',
    keywords: ['regular expression', 'pattern match', 'find text'],
    search: {
      aliases: [
        'regular expression tester',
        'pattern tester',
        'regex examples',
        'regular expression library',
      ],
      actions: ['test', 'match', 'find', 'browse'],
      inputs: ['regex', 'regular expression', 'text'],
      outputs: ['matches', 'regex pattern', 'pattern'],
      tasks: ['test regex against text', 'find pattern matches', 'browse common regex patterns'],
    },
  },

  // Image
  {
    id: 'image-converter',
    name: 'Image Resizer, Converter & Compressor',
    description: 'Resize, convert, compress, rotate, and flip single or batch images',
    path: '/image-converter',
    icon: Image,
    category: 'image',
    keywords: ['resize images', 'compress images', 'rotate photos', 'png to jpg', 'jpg to webp'],
    search: {
      aliases: [
        'photo format converter',
        'image resizer',
        'image compressor',
        'batch image editor',
      ],
      actions: ['resize', 'compress', 'convert', 'rotate', 'flip', 'optimize'],
      inputs: ['image', 'photo', 'png', 'jpg', 'webp'],
      outputs: ['resized image', 'compressed image', 'png', 'jpg', 'webp'],
      qualifiers: ['smaller', 'dimensions', 'quality', 'batch'],
      tasks: [
        'convert png to jpg',
        'change image to webp',
        'make image smaller',
        'resize multiple photos',
        'rotate and flip photos',
      ],
    },
  },
  {
    id: 'image-merger',
    name: 'Image Merger',
    description: 'Merge multiple images in split layouts (2 or 4 images)',
    path: '/image-merger',
    icon: Grid2x2,
    category: 'image',
    keywords: ['combine images', 'photo collage', 'join pictures'],
    search: {
      aliases: [
        'photo merger',
        'image collage',
        'image combiner',
        'photo joiner',
        'picture combiner',
      ],
      actions: ['merge', 'combine', 'join'],
      inputs: ['image', 'photo', 'picture'],
      outputs: ['merged image', 'collage'],
      qualifiers: ['two images', 'four images', 'layout'],
      tasks: ['combine four photos', 'join two images'],
    },
  },
  {
    id: 'svg-optimizer',
    name: 'SVG Optimizer',
    description: 'Optimize and minify SVG files',
    path: '/svg-optimizer',
    icon: Scissors,
    category: 'image',
    keywords: ['compress svg', 'minify vector', 'make svg smaller'],
    search: {
      aliases: ['svg compressor', 'vector optimizer'],
      actions: ['optimize', 'compress', 'minify'],
      inputs: ['svg', 'vector'],
      outputs: ['svg'],
      qualifiers: ['smaller'],
      tasks: ['reduce svg file size', 'minify vector graphic'],
    },
  },
  {
    id: 'image-enhancer',
    name: 'Image Quality Enhancer & Upscaler',
    description:
      'Upscale resolution (up to 4x), sharpen blurry edges, and enhance contrast in real-time',
    path: '/image-enhancer',
    icon: Sparkles,
    category: 'image',
    containerWidth: 'full',
    keywords: [
      'upscale image',
      'enhance image quality',
      'sharpen photo',
      'increase resolution',
      'photo restoration',
      'unsharp mask',
    ],
    search: {
      aliases: ['image upscaler', 'photo enhancer', 'quality booster'],
      actions: ['enhance', 'upscale', 'sharpen', 'restore'],
      inputs: ['image', 'photo', 'png', 'jpg', 'webp'],
      outputs: ['enhanced image', 'upscaled photo'],
      qualifiers: ['hd', '4k', 'sharper', 'higher resolution'],
      tasks: ['upscale image resolution', 'sharpen blurry photo', 'enhance image quality'],
    },
  },

  // Time
  {
    id: 'timestamp',
    name: 'Unix Timestamp Converter',
    description: 'Convert Unix seconds or milliseconds to readable dates and back',
    path: '/timestamp',
    icon: Clock,
    category: 'time',
    keywords: ['epoch', 'date', 'time', 'unix seconds', 'milliseconds'],
    search: {
      aliases: ['epoch converter', 'unix time converter', 'linux timestamp to date', 'ts to date'],
      actions: ['convert'],
      inputs: ['timestamp', 'epoch', 'date', 'unix time'],
      outputs: ['timestamp', 'date', 'unix time'],
      tasks: [
        'convert epoch to date',
        'turn date into unix timestamp',
        'milisaniyeyi tarihe cevir',
      ],
    },
  },
  {
    id: 'time-zone-converter',
    name: 'Time Zone Converter',
    description: 'Convert local times between world time zones with daylight-saving support',
    path: '/time-zone-converter',
    icon: Globe2,
    category: 'time',
    keywords: ['timezone', 'world clock', 'utc', 'gmt', 'dst', 'saat dilimi', 'saat farki'],
    search: {
      aliases: ['world time converter', 'timezone calculator', 'saat dilimi cevirici'],
      actions: ['convert', 'compare'],
      inputs: ['date', 'time', 'timezone', 'local time'],
      outputs: ['local time', 'utc time', 'unix timestamp'],
      qualifiers: ['dst', 'daylight saving', 'city time'],
      tasks: [
        'convert time between countries',
        'find meeting time in another timezone',
        'istanbul saati new yorkta kac',
      ],
    },
  },
  {
    id: 'date-calculator',
    name: 'Date Difference & Calculator',
    description: 'Count days between dates or add and subtract days, weeks, months, and years',
    path: '/date-calculator',
    icon: CalendarRange,
    category: 'time',
    keywords: ['date difference', 'days between', 'add days', 'subtract date', 'iki tarih arasi'],
    search: {
      aliases: ['days between dates', 'date duration calculator', 'tarih hesaplama'],
      actions: ['calculate', 'count', 'add', 'subtract'],
      inputs: ['date', 'start date', 'end date', 'days'],
      outputs: ['day count', 'duration', 'calculated date'],
      qualifiers: ['calendar days', 'weeks', 'months', 'years'],
      tasks: [
        'calculate days between two dates',
        'add thirty days to a date',
        'iki tarih arasinda kac gun var',
      ],
    },
  },
  {
    id: 'time-unit-converter',
    name: 'Time Unit & Tick Converter',
    description: 'Convert duration units and .NET ticks to dates or Unix timestamps',
    path: '/time-unit-converter',
    icon: TimerReset,
    category: 'time',
    keywords: [
      'seconds to minutes',
      'milliseconds to seconds',
      'nanoseconds',
      'duration',
      '.net ticks',
      '100 nanoseconds',
      'saniye dakika',
      'milisaniye saniye',
    ],
    search: {
      aliases: ['duration converter', 'time unit converter', 'tick converter', '.net ticks'],
      actions: ['convert', 'calculate'],
      inputs: ['nanoseconds', 'milliseconds', 'seconds', 'minutes', 'hours', 'ticks'],
      outputs: ['duration', 'date', 'unix timestamp', '.net ticks'],
      qualifiers: ['all equivalents', '100 nanoseconds', '.net'],
      tasks: [
        'convert seconds to minutes',
        'convert milliseconds to seconds',
        'convert .net ticks to date',
        'saniyeyi dakikaya cevir',
        'milisaniyeyi saniyeye cevir',
      ],
    },
  },

  // Color
  {
    id: 'color',
    name: 'Color Converter',
    description: 'Convert colors (Hex, RGB, HSL)',
    path: '/color',
    icon: Palette,
    category: 'color',
    keywords: ['wcag', 'accessibility', 'color scheme', 'contrast checker'],
    search: {
      aliases: ['hex rgb converter', 'colour converter'],
      actions: ['convert'],
      inputs: ['color', 'hex', 'rgb', 'hsl'],
      outputs: ['hex', 'rgb', 'hsl'],
      tasks: ['convert hex to rgb', 'change rgb to hsl'],
    },
  },
  {
    id: 'color-palette',
    name: 'Color Palette & Contrast',
    description: 'Generate color palettes and check contrast',
    path: '/color-palette',
    icon: Palette,
    category: 'color',
    search: {
      aliases: ['contrast checker', 'color scheme generator'],
      actions: ['generate', 'check'],
      inputs: ['color', 'hex'],
      outputs: ['palette', 'contrast ratio', 'color scheme'],
      qualifiers: ['wcag', 'accessibility'],
      tasks: ['check color contrast', 'generate matching color palette'],
    },
  },

  // Development
  {
    id: 'git-utils',
    name: 'Git Helpers',
    description: '.gitignore generator, commit validator, URL parser',
    path: '/git-utils',
    icon: GitBranch,
    category: 'development',
    keywords: ['gitignore', 'commit message', 'repository url'],
    search: {
      aliases: ['git utility', 'gitignore generator'],
      actions: ['generate', 'validate', 'parse'],
      inputs: ['git', 'commit message', 'repository url'],
      outputs: ['gitignore', 'validated commit', 'parsed url'],
      tasks: ['create gitignore', 'validate commit message'],
    },
  },
  {
    id: 'json-beautifier',
    name: 'JSON Beautifier & Validator',
    description: 'Format, validate, safely repair, minify, and sort JSON locally in your browser',
    path: '/json-beautifier',
    icon: Braces,
    category: 'development',
    keywords: [
      'pretty json',
      'prettify json',
      'format json',
      'fix invalid json',
      'make json readable',
      'json lint',
      'json parser',
      'beatify json',
    ],
    search: {
      aliases: ['json formatter', 'json lint', 'json validator'],
      actions: ['beautify', 'format', 'validate', 'minify', 'sort', 'fix'],
      inputs: ['json', 'api response'],
      outputs: ['formatted json', 'minified json'],
      qualifiers: ['readable', 'invalid', 'broken'],
      tasks: ['make json readable', 'find json syntax error', 'fix broken json'],
    },
  },
  {
    id: 'code-formatter',
    name: 'Code Formatter',
    description: 'Format JavaScript, HTML, CSS, and JSON; minify JavaScript or JSON',
    path: '/code-formatter',
    icon: Code,
    category: 'development',
    keywords: ['prettier', 'beautify code', 'format javascript', 'format css', 'minify javascript'],
    search: {
      aliases: ['prettier online', 'source formatter'],
      actions: ['format', 'beautify', 'minify'],
      inputs: ['javascript', 'html', 'css', 'code'],
      outputs: ['formatted code', 'minified code'],
      tasks: ['format javascript code', 'beautify css'],
    },
  },
  {
    id: 'html-preview',
    name: 'HTML Viewer & Playground',
    description:
      'Build and preview HTML, CSS, and JavaScript, then export a single file or project ZIP',
    path: '/html-preview',
    icon: Code2,
    category: 'development',
    keywords: [
      'html viewer',
      'html preview',
      'render html',
      'run html',
      'view html output',
      'online html editor',
      'css playground',
      'javascript sandbox',
    ],
    search: {
      aliases: ['html viewer', 'html playground', 'online html editor'],
      actions: ['preview', 'view', 'render', 'run', 'edit'],
      inputs: ['html', 'css', 'javascript'],
      outputs: ['html preview', 'web page', 'single html file', 'project zip'],
      qualifiers: ['live', 'sandbox'],
      tasks: ['view html output', 'run html and css', 'preview web page', 'export html project'],
    },
  },

  // Data
  {
    id: 'json-schema',
    name: 'JSON Schema Generator & Validator',
    description:
      'Infer JSON Schema from JSON payload and validate JSON against schema specifications',
    path: '/json-schema',
    icon: FileJson,
    category: 'data',
    keywords: ['json schema', 'schema generator', 'json validator', 'infer schema', 'draft-07'],
    search: {
      aliases: ['json schema builder', 'json schema validator', 'schema inferrer'],
      actions: ['generate', 'validate', 'infer', 'check'],
      inputs: ['json document', 'json schema'],
      outputs: ['json schema draft', 'validation errors'],
      tasks: ['generate json schema from json', 'validate json against schema'],
    },
  },
  {
    id: 'sql-formatter',
    name: 'SQL Formatter',
    description: 'Format and validate SQL queries',
    path: '/sql-formatter',
    icon: Database,
    category: 'data',
    keywords: ['pretty sql', 'beautify query', 'format query'],
    search: {
      aliases: ['sql beautifier', 'query formatter'],
      actions: ['format', 'beautify', 'validate'],
      inputs: ['sql', 'query'],
      outputs: ['formatted sql'],
      tasks: ['make sql query readable', 'format select statement'],
    },
  },
  {
    id: 'csv-enhanced',
    name: 'CSV Toolkit',
    description: 'CSV to JSON, Transpose, and more',
    path: '/csv-enhanced',
    icon: FileSpreadsheet,
    category: 'data',
    search: {
      aliases: ['csv tools', 'csv editor'],
      actions: ['convert', 'transpose', 'inspect'],
      inputs: ['csv'],
      outputs: ['json', 'csv', 'transposed csv'],
      tasks: ['convert csv to json', 'transpose csv rows'],
    },
  },
  {
    id: 'csv-to-sql',
    name: 'CSV to SQL Generator',
    description:
      'Convert CSV data to SQL schema with intelligent type inference and multiple database dialects',
    path: '/csv-to-sql',
    icon: Database,
    category: 'data',
    search: {
      aliases: ['csv database importer', 'insert generator'],
      actions: ['convert', 'generate'],
      inputs: ['csv'],
      outputs: ['sql', 'sql schema', 'insert statement'],
      qualifiers: ['postgresql', 'mysql', 'sqlite', 'dialect'],
      tasks: ['convert csv into postgres insert', 'generate sql table from csv'],
    },
  },
  {
    id: 'mock-api',
    name: 'Temporary Mock API',
    description: 'Create a short-lived JSON endpoint for testing API clients and integrations',
    path: '/mock-api',
    icon: Globe,
    category: 'network',
    keywords: ['mock server', 'mock endpoint', 'fake api', 'temporary api', 'postman mock'],
    search: {
      aliases: ['mock server', 'mock endpoint generator', 'fake rest api'],
      actions: ['create', 'mock', 'test', 'simulate'],
      inputs: ['http method', 'path', 'json response'],
      outputs: ['temporary endpoint', 'mock api url', 'json response'],
      qualifiers: ['short-lived', 'json', 'stateless'],
      tasks: [
        'create a mock api endpoint',
        'simulate a rest api response',
        'test with a fake endpoint',
      ],
    },
  },
  {
    id: 'scanned-pdf-converter',
    name: 'Image & PDF Scan Effect Generator',
    description:
      'Apply adjustable scan-style visual effects to JPG, PNG, BMP, TIFF, and PDF files for labeled mockups',
    path: '/scanned-pdf-converter',
    icon: File,
    category: 'image',
    keywords: ['scan image effect', 'document mockup', 'jpg png', 'fax texture', 'scan-style pdf'],
    search: {
      aliases: ['scan effect', 'document photo effect', 'pdf scan effect', 'scan mockup'],
      actions: ['convert', 'stylize'],
      inputs: ['image', 'jpg', 'png', 'photo', 'pdf'],
      outputs: ['scan-effect image', 'scan-style pdf preview', 'mockup file'],
      qualifiers: ['scan-style', 'visual effect'],
      tasks: [
        'apply a scan-style effect to an image',
        'create a paper texture preview for a pdf',
        'prepare a labeled scan-effect mockup',
      ],
    },
  },

  // Security
  {
    id: 'jwt-enhanced',
    name: 'JWT Decoder, Generator & Verifier',
    description: 'Decode, verify, and generate JWTs',
    path: '/jwt-enhanced',
    icon: Lock,
    category: 'security',
    keywords: ['json web token', 'token', 'claims'],
    search: {
      aliases: ['json web token tool', 'jwt debugger'],
      actions: ['decode', 'verify', 'generate', 'inspect'],
      inputs: ['jwt', 'token', 'bearer token'],
      outputs: ['claims', 'jwt', 'verified token'],
      tasks: ['see inside jwt token', 'verify jwt signature', 'generate jwt'],
    },
  },
  {
    id: 'password-generator',
    name: 'Password Generator',
    description: 'Generate secure passwords with customizable options',
    path: '/password-generator',
    icon: KeyRound,
    category: 'security',
    keywords: ['strong password', 'random password', 'guclu sifre', 'sifre olustur', 'parola uret'],
    search: {
      aliases: ['random password maker', 'sifre olusturucu'],
      actions: ['generate', 'create'],
      outputs: ['password', 'passphrase'],
      qualifiers: ['strong', 'secure', 'random'],
      tasks: ['generate strong password', 'guclu sifre olustur'],
    },
  },
  {
    id: 'ssl-cert-gen',
    name: 'SSL & OpenSSL Setup Guide',
    description:
      'Generate Certbot commands, Let’s Encrypt configs, and OpenSSL self-signed certificate commands',
    path: '/ssl-cert-gen',
    icon: Shield,
    category: 'security',
    search: {
      aliases: [
        'certbot guide',
        'lets encrypt setup',
        'ssl setup guide',
        'self signed ssl command',
      ],
      actions: ['configure', 'install', 'generate', 'create'],
      inputs: ['domain', 'web server', 'operating system'],
      outputs: ['certbot command', 'https setup steps', 'openssl command'],
      tasks: [
        'set up lets encrypt certificate',
        'generate certbot command',
        'create self signed ssl command',
      ],
    },
  },
  {
    id: 'file-encryption',
    name: 'File Encryption & Decryption',
    description: 'Encrypt and decrypt any file locally with a passphrase using AES-256-GCM',
    path: '/file-encryption',
    icon: FileLock2,
    category: 'security',
    keywords: ['encrypt file', 'decrypt file', 'aes-256-gcm', 'file encryption'],
    search: {
      aliases: ['encrypt file online', 'decrypt file online', 'file cipher'],
      actions: ['encrypt', 'decrypt'],
      inputs: ['file', 'document', 'photo'],
      outputs: ['encrypted file', 'decrypted file'],
      qualifiers: ['aes', 'aes-256-gcm', 'passphrase'],
      tasks: ['encrypt a file with a password', 'decrypt an encrypted file'],
    },
  },

  // Network
  {
    id: 'api-request',
    name: 'HTTP Request & API Collection Tester',
    description: 'Send individual API requests or run reusable request collections with assertions',
    path: '/api-request',
    icon: Globe,
    category: 'network',
    keywords: ['api', 'rest', 'fetch', 'curl', 'request builder'],
    search: {
      aliases: [
        'rest client',
        'api client',
        'http tester',
        'api collection builder',
        'no code api tester',
        'http collection manager',
      ],
      actions: ['send', 'test', 'request', 'fetch', 'build', 'manage'],
      inputs: ['api', 'url', 'http request', 'curl', 'collection'],
      outputs: ['http response', 'api response', 'request collection'],
      qualifiers: ['get', 'post', 'put', 'delete', 'visual', 'cors'],
      tasks: [
        'send api request',
        'test rest endpoint',
        'build api test collection',
        'create reusable http requests',
        'inspect api response',
      ],
    },
  },
  {
    id: 'websocket',
    name: 'WebSocket Tester',
    description: 'Test WebSocket connections',
    path: '/websocket',
    icon: Radio,
    category: 'network',
    keywords: ['socket', 'realtime connection', 'ws connection'],
    search: {
      aliases: ['web socket client', 'ws tester'],
      actions: ['connect', 'test', 'send'],
      inputs: ['websocket', 'ws url', 'socket'],
      outputs: ['websocket messages', 'connection status'],
      qualifiers: ['realtime'],
      tasks: ['test websocket connection', 'connect to ws server'],
    },
  },
  {
    id: 'dns-lookup',
    name: 'DNS Lookup',
    description: 'Query DNS records from public recursive resolvers over HTTPS',
    path: '/dns-lookup',
    icon: Network,
    category: 'network',
    keywords: ['domain ip', 'nameserver', 'dns records', 'resolve domain'],
    search: {
      aliases: ['domain lookup', 'dns checker'],
      actions: ['lookup', 'resolve', 'troubleshoot'],
      inputs: ['domain', 'hostname', 'dns'],
      outputs: ['ip address', 'dns records', 'nameserver'],
      tasks: ['find domain ip address', 'check dns records'],
    },
  },
  {
    id: 'what-is-my-ip',
    name: 'What Is My IP & Network Info',
    description: 'Check your IP or look up an address and run an optional WebRTC leak test',
    path: '/what-is-my-ip',
    icon: Wifi,
    category: 'network',
    keywords: [
      'what is my ip',
      'my ip address',
      'ip lookup',
      'ip adresim',
      'public ip',
      'ipv4 ipv6',
      'isp lookup',
      'ip konumu',
    ],
    search: {
      aliases: ['my ip', 'ip checker', 'ip lookup', 'ip adresim nedir'],
      actions: ['check', 'lookup', 'find', 'detect'],
      inputs: ['ip address', 'domain', 'hostname'],
      outputs: ['public ip', 'ipv4', 'ipv6', 'isp', 'country', 'city', 'webrtc leak'],
      tasks: ['find my public ip address', 'check my ip location', 'ip adresimi ogren'],
    },
  },
  {
    id: 'speed-test',
    name: 'Internet Speed & Latency Test',
    description: 'Test your download speed, upload speed, ping latency, and jitter in real-time',
    path: '/speed-test',
    icon: Zap,
    category: 'network',
    keywords: [
      'speed test',
      'internet speed',
      'bandwidth test',
      'ping test',
      'download speed',
      'upload speed',
      'hiz testi',
      'internet hiz testi',
    ],
    search: {
      aliases: ['speedtest', 'bandwidth tester', 'internet hiz testi'],
      actions: ['test', 'measure', 'calculate', 'benchmark'],
      inputs: ['internet connection'],
      outputs: ['download mbps', 'upload mbps', 'ping ms', 'jitter ms'],
      tasks: ['test internet speed', 'check download speed', 'internet hizimi olc'],
    },
  },
  {
    id: 'curl-to-code',
    name: 'cURL to Code Converter',
    description:
      'Convert cURL commands to JavaScript fetch or Axios, Node.js native HTTP, Python requests, Go net/http, PHP cURL, and cleaned-up Bash cURL code',
    path: '/curl-to-code',
    icon: Terminal,
    category: 'converters',
    keywords: ['curl', 'fetch', 'axios', 'python requests', 'api request', 'convert curl'],
    search: {
      aliases: ['curl converter', 'curl to fetch', 'curl to python'],
      actions: ['convert', 'parse', 'transform', 'generate'],
      inputs: ['curl command', 'http request'],
      outputs: [
        'javascript fetch',
        'javascript axios',
        'node.js native http',
        'python requests',
        'go net/http',
        'php cURL',
        'bash cURL',
      ],
      tasks: ['convert curl to python', 'convert curl to fetch', 'generate api code from curl'],
    },
  },
  {
    id: 'chmod-calculator',
    name: 'Chmod Calculator',
    description:
      'Calculate Unix permissions with octal, symbolic, special-bit, and chmod command outputs.',
    path: '/chmod-calculator',
    icon: ShieldCheck,
    category: 'security',
    keywords: [
      'chmod',
      'permissions',
      'octal',
      'rwxr-xr-x',
      'linux permissions',
      'setuid',
      'setgid',
      'sticky bit',
      '755',
      '644',
    ],
    search: {
      aliases: ['permission calculator', 'chmod generator', 'file permissions'],
      actions: ['calculate', 'generate', 'convert'],
      inputs: [
        'permission matrix',
        'octal value',
        'symbolic string',
        'special bits',
        'target path',
      ],
      outputs: ['octal permission', 'symbolic notation', 'chmod command'],
      tasks: ['calculate chmod permission', 'convert 755 to symbolic', 'generate chmod command'],
    },
  },
  {
    id: 'opengraph-preview',
    name: 'OpenGraph & Social Card Previewer',
    description:
      'Generate OpenGraph meta tags and preview Twitter, Facebook, LinkedIn & Discord cards',
    path: '/opengraph-preview',
    icon: Share2,
    category: 'network',
    keywords: ['open graph', 'og tags', 'social card', 'twitter card', 'meta tags', 'serp preview'],
    search: {
      aliases: ['og tag generator', 'social media preview', 'twitter card preview'],
      actions: ['preview', 'generate', 'inspect', 'test'],
      inputs: ['page title', 'description', 'og image', 'url'],
      outputs: ['meta tags html', 'twitter card mockup', 'facebook preview'],
      tasks: ['preview social card', 'generate open graph meta tags'],
    },
  },
  {
    id: 'svg-to-react',
    name: 'SVG to React & JSX Converter',
    description: 'Convert raw SVG markup into clean React, Vue, Svelte, and TSX components',
    path: '/svg-to-react',
    icon: Code2,
    category: 'converters',
    keywords: ['svg', 'react component', 'jsx', 'tsx', 'vue component', 'svg converter'],
    search: {
      aliases: ['svg to jsx', 'svg to tsx', 'svg icon converter'],
      actions: ['convert', 'transform', 'clean', 'format'],
      inputs: ['svg markup', 'svg file'],
      outputs: ['react component', 'vue component', 'svelte component', 'jsx'],
      tasks: ['convert svg to react', 'transform svg to tsx component'],
    },
  },
  {
    id: 'subnet-calculator',
    name: 'Subnet & CIDR IP Calculator',
    description: 'Calculate IP subnet mask, broadcast address, CIDR range, and usable host count',
    path: '/subnet-calculator',
    icon: Network,
    category: 'network',
    keywords: [
      'subnet',
      'cidr',
      'ip calculator',
      'netmask',
      'broadcast ip',
      'usable hosts',
      'network range',
    ],
    search: {
      aliases: ['ip subnet calculator', 'cidr calculator', 'netmask calculator'],
      actions: ['calculate', 'inspect', 'convert'],
      inputs: ['ip address', 'cidr mask'],
      outputs: ['network address', 'broadcast address', 'usable ip range', 'binary subnet'],
      tasks: ['calculate subnet range', 'find broadcast ip', 'calculate cidr block'],
    },
  },
  {
    id: 'bcrypt-generator',
    name: 'Bcrypt Hash Generator & Verifier',
    description: 'Generate secure Bcrypt password hashes and verify hashes against plain text',
    path: '/bcrypt-generator',
    icon: Lock,
    category: 'security',
    keywords: ['bcrypt', 'password hash', 'salt rounds', 'hash verifier', 'crypto hash'],
    search: {
      aliases: ['bcrypt generator', 'bcrypt checker', 'password hash tool'],
      actions: ['hash', 'verify', 'generate', 'compare'],
      inputs: ['password', 'bcrypt hash'],
      outputs: ['bcrypt hash', 'verification match status'],
      tasks: ['hash password with bcrypt', 'verify bcrypt hash'],
    },
  },
  {
    id: 'markdown-table-generator',
    name: 'Markdown Table Builder & Converter',
    description: 'Interactive Markdown table builder and CSV-to-Markdown converter',
    path: '/markdown-table-generator',
    icon: Table,
    category: 'text',
    keywords: ['markdown table', 'csv to markdown', 'table builder'],
    search: {
      aliases: ['markdown table generator', 'csv to markdown table', 'table builder'],
      actions: ['create', 'convert', 'format', 'build'],
      inputs: ['table data', 'csv string'],
      outputs: ['markdown table text'],
      tasks: ['generate markdown table', 'convert csv to markdown table'],
    },
  },
  {
    id: 'cron-parser',
    name: 'Cron Parser & Execution Scheduler',
    description:
      'Parse CRON expressions into human language and calculate upcoming execution schedules',
    path: '/cron-parser',
    icon: Clock,
    category: 'development',
    keywords: [
      'cron parser',
      'cron humanizer',
      'cron schedule',
      'cron next executions',
      'cron expression',
    ],
    search: {
      aliases: ['cron explainer', 'cron scheduler', 'cron humanizer'],
      actions: ['parse', 'explain', 'schedule', 'humanize'],
      inputs: ['cron expression'],
      outputs: ['human description', 'upcoming execution dates'],
      tasks: ['explain cron expression', 'calculate next cron runs'],
    },
  },
  {
    id: 'docker-run-to-compose',
    name: 'Docker Run to Docker Compose Converter',
    description: 'Convert CLI docker run commands to clean docker-compose.yml files',
    path: '/docker-run-to-compose',
    icon: Database,
    category: 'development',
    keywords: ['docker run', 'docker compose', 'yaml', 'container', 'docker CLI'],
    search: {
      aliases: ['docker run to compose', 'docker compose generator'],
      actions: ['convert', 'generate', 'transform'],
      inputs: ['docker run command'],
      outputs: ['docker-compose.yml'],
      tasks: ['convert docker run to compose', 'generate compose file'],
    },
  },
  {
    id: 'json-to-typescript',
    name: 'JSON to TypeScript & Zod Converter',
    description: 'Generate TypeScript interfaces and Zod validation schemas from JSON payload',
    path: '/json-to-typescript',
    icon: Code2,
    category: 'converters',
    keywords: ['json to ts', 'typescript interface', 'zod schema', 'type generator'],
    search: {
      aliases: ['json to typescript', 'json to zod', 'type generator'],
      actions: ['convert', 'generate', 'infer'],
      inputs: ['json'],
      outputs: ['typescript interface', 'zod schema'],
      tasks: ['generate typescript interface from json', 'convert json to zod'],
    },
  },
  {
    id: 'css-to-tailwind',
    name: 'CSS to Tailwind CSS Converter',
    description: 'Convert traditional CSS properties into utility Tailwind CSS classes',
    path: '/css-to-tailwind',
    icon: Palette,
    category: 'converters',
    keywords: ['css to tailwind', 'tailwind converter', 'utility classes', 'css transform'],
    search: {
      aliases: ['css to tailwind converter', 'tailwind helper'],
      actions: ['convert', 'transform'],
      inputs: ['css rules'],
      outputs: ['tailwind class names'],
      tasks: ['convert css to tailwind', 'transform css properties to tailwind'],
    },
  },
  {
    id: 'ssh-key-generator',
    name: 'Ed25519 & RSA SSH Key Generator',
    description: 'Browser-side OpenSSH key pair generator using WebCrypto API',
    path: '/ssh-key-generator',
    icon: KeyRound,
    category: 'security',
    keywords: ['ssh key', 'ed25519', 'rsa 4096', 'public key', 'private key', 'openssh'],
    search: {
      aliases: ['ssh key generator', 'ed25519 generator', 'openssh key maker'],
      actions: ['generate', 'create', 'download'],
      inputs: ['key type', 'passphrase'],
      outputs: ['public key', 'private key'],
      tasks: ['generate ssh key pair', 'create ed25519 ssh key'],
    },
  },
  {
    id: 'color-contrast-checker',
    name: 'WCAG Color Contrast Checker',
    description: 'Calculate color contrast ratio and verify WCAG AA/AAA accessibility compliance',
    path: '/color-contrast-checker',
    icon: Palette,
    category: 'color',
    keywords: ['color contrast', 'wcag', 'accessibility', 'a11y', 'contrast ratio'],
    search: {
      aliases: ['wcag contrast checker', 'color accessibility test'],
      actions: ['calculate', 'check', 'verify'],
      inputs: ['foreground color', 'background color'],
      outputs: ['contrast ratio', 'wcag compliance status'],
      tasks: ['check color contrast', 'verify wcag aa compliance'],
    },
  },
  {
    id: 'htpasswd-generator',
    name: 'HTPASSWD Basic Auth Generator',
    description: 'Generate Nginx and Apache .htpasswd entries with bcrypt or MD5 hashing',
    path: '/htpasswd-generator',
    icon: Lock,
    category: 'security',
    keywords: ['htpasswd', 'nginx basic auth', 'apache auth', 'bcrypt htpasswd', 'password hash'],
    search: {
      aliases: ['htpasswd generator', 'basic auth hash generator'],
      actions: ['generate', 'hash', 'create'],
      inputs: ['username', 'password'],
      outputs: ['.htpasswd entry'],
      tasks: ['generate htpasswd line', 'create nginx basic auth password'],
    },
  },
  {
    id: 'timezone-meeting-planner',
    name: 'World Timezone & Meeting Planner',
    description: 'Plan meetings across global time zones by comparing working hour overlaps',
    path: '/timezone-meeting-planner',
    icon: Globe,
    category: 'time',
    keywords: ['timezone planner', 'meeting scheduler', 'remote work hours', 'world clock overlap'],
    search: {
      aliases: ['timezone meeting planner', 'world team schedule'],
      actions: ['plan', 'compare', 'schedule'],
      inputs: ['cities', 'timezones'],
      outputs: ['overlapping work hours', 'best meeting slots'],
      tasks: ['plan meeting across timezones', 'find overlapping work hours'],
    },
  },
  {
    id: 'git-command-generator',
    name: 'Git Interactive Command Generator',
    description: 'Interactive builder for complex Git workflow commands and undo actions',
    path: '/git-command-generator',
    icon: GitBranch,
    category: 'development',
    keywords: ['git commands', 'git undo', 'git rebase', 'git reset', 'git cheat sheet'],
    search: {
      aliases: ['git command builder', 'git undo helper', 'git helper'],
      actions: ['generate', 'build', 'explain'],
      inputs: ['git task', 'branch name'],
      outputs: ['git terminal command'],
      tasks: ['undo last git commit', 'rebase interactive command'],
    },
  },
  {
    id: 'regex-visualizer',
    name: 'Regex Pattern Visualizer',
    description:
      'Inspect JavaScript regular expressions with ordered token explanations and syntax validation',
    path: '/regex-visualizer',
    icon: Regex,
    category: 'development',
    keywords: ['regex visualizer', 'regex diagram', 'regexp tree', 'regex explainer'],
    search: {
      aliases: ['regex graph', 'regex visualizer', 'regex syntax tree'],
      actions: ['visualize', 'parse', 'explain'],
      inputs: ['regex pattern'],
      outputs: ['visual diagram', 'token breakdown'],
      tasks: ['visualize regex pattern', 'explain regex structure'],
    },
  },
  {
    id: 'user-agent-parser',
    name: 'User-Agent String Parser',
    description:
      'Identify a User-Agent string’s browser family, operating system, and device class',
    path: '/user-agent-parser',
    icon: FileSearch,
    category: 'network',
    keywords: ['user agent parser', 'ua inspector', 'browser detection', 'device info'],
    search: {
      aliases: ['user agent inspector', 'ua parser'],
      actions: ['parse', 'inspect', 'detect'],
      inputs: ['user agent string'],
      outputs: ['browser name', 'os version', 'device type'],
      tasks: ['parse user agent header', 'detect browser and os'],
    },
  },
  {
    id: 'fancy-font-generator',
    name: 'Unicode Fancy Font Generator',
    description: 'Convert text to Unicode aesthetic styles for Instagram bios, TikTok, and Twitter',
    path: '/fancy-font-generator',
    icon: Type,
    category: 'text',
    keywords: ['fancy font', 'unicode text', 'instagram bio font', 'aesthetic text', 'cool text'],
    search: {
      aliases: ['fancy text generator', 'instagram font generator', 'aesthetic font maker'],
      actions: ['generate', 'convert', 'copy'],
      inputs: ['text'],
      outputs: ['fancy unicode fonts'],
      tasks: ['generate fancy fonts for instagram', 'create aesthetic text'],
    },
  },
  {
    id: 'instagram-line-breaker',
    name: 'Instagram & TikTok Caption Line Breaker',
    description: 'Format social media captions with invisible clean line breaks',
    path: '/instagram-line-breaker',
    icon: FileText,
    category: 'text',
    keywords: [
      'instagram line break',
      'tiktok caption break',
      'caption formatter',
      'space generator',
    ],
    search: {
      aliases: ['ig line breaker', 'caption spaces generator'],
      actions: ['format', 'clean', 'space'],
      inputs: ['caption text'],
      outputs: ['formatted caption with line breaks'],
      tasks: ['add line breaks to instagram caption', 'format tiktok bio'],
    },
  },
  {
    id: 'aspect-ratio-calculator',
    name: 'Social Media Aspect Ratio Calculator',
    description: 'Calculate pixel dimensions and aspect ratios for YouTube, TikTok, and Instagram',
    path: '/aspect-ratio-calculator',
    icon: Calculator,
    category: 'image',
    keywords: [
      'aspect ratio',
      '16:9',
      '9:16',
      '1:1',
      '4:5',
      'image dimensions',
      'video resolution',
    ],
    search: {
      aliases: ['aspect ratio calculator', 'social media image resizer specs'],
      actions: ['calculate', 'resize', 'convert'],
      inputs: ['width', 'height', 'ratio'],
      outputs: ['scaled dimensions', 'preset resolution'],
      tasks: ['calculate 16:9 dimensions', 'find 9:16 reels resolution'],
    },
  },
  {
    id: 'twitter-thread-splitter',
    name: 'Twitter / X Thread Splitter',
    description:
      'Automatically split long articles or posts into numbered 280-character tweet threads',
    path: '/twitter-thread-splitter',
    icon: Scissors,
    category: 'text',
    keywords: ['twitter thread', 'x thread splitter', 'tweet splitter', 'long post to tweets'],
    search: {
      aliases: ['tweet thread generator', 'twitter thread maker'],
      actions: ['split', 'format', 'count'],
      inputs: ['long article text'],
      outputs: ['numbered tweet blocks (1/N)'],
      tasks: ['split text into tweets', 'make twitter thread'],
    },
  },
  {
    id: 'og-image-extractor',
    name: 'OpenGraph & Social Image Extractor',
    description:
      'Extract OpenGraph thumbnail images, titles, and social metadata from website URLs',
    path: '/og-image-extractor',
    icon: FileImage,
    category: 'network',
    keywords: ['og image extractor', 'social image downloader', 'meta tag inspector'],
    search: {
      aliases: ['og image finder', 'social card image extractor'],
      actions: ['extract', 'inspect', 'preview'],
      inputs: ['url', 'meta tag html'],
      outputs: ['og image url', 'page title', 'meta tags'],
      tasks: ['extract og image from website', 'find twitter card image'],
    },
  },
  {
    id: 'llm-token-counter',
    name: 'LLM Token Counter & Price Estimator',
    description:
      'Estimate prompt tokens and standard API costs across current OpenAI, Anthropic, Google, DeepSeek, and Mistral models',
    path: '/llm-token-counter',
    icon: Code2,
    category: 'ai',
    keywords: [
      'token counter',
      'gpt-6 tokens',
      'claude 5.5 tokens',
      'llm cost estimator',
      'api price',
    ],
    search: {
      aliases: ['llm token counter', 'gpt token estimator', 'claude price calculator'],
      actions: ['count', 'estimate', 'calculate'],
      inputs: ['prompt text', 'code snippet'],
      outputs: ['token count', 'estimated api cost ($)'],
      tasks: ['count tokens for gpt-4', 'estimate claude api cost'],
    },
  },
  {
    id: 'codebase-to-prompt',
    name: 'Codebase to Prompt Packager',
    description:
      'Manually combine selected code snippets and file paths into a copyable XML prompt',
    path: '/codebase-to-prompt',
    icon: FileCode,
    category: 'ai',
    keywords: ['codebase to prompt', 'repo to prompt', 'llm code packager', 'chatgpt code helper'],
    search: {
      aliases: ['code to prompt', 'repo packager for ai', 'codebase prompt maker'],
      actions: ['package', 'format', 'bundle'],
      inputs: ['source code files'],
      outputs: ['formatted xml prompt'],
      tasks: ['convert codebase into llm prompt', 'bundle files for chatgpt'],
    },
  },
  {
    id: 'system-prompt-builder',
    name: 'AI System Prompt & Persona Builder',
    description:
      'Visual builder for structured AI System Prompts (Role, Constraints, Output Specs, Rules)',
    path: '/system-prompt-builder',
    icon: Sparkles,
    category: 'ai',
    keywords: [
      'system prompt builder',
      'ai persona generator',
      'chatgpt system message',
      'prompt engineering',
    ],
    search: {
      aliases: ['system prompt generator', 'ai role maker', 'prompt persona builder'],
      actions: ['build', 'create', 'generate'],
      inputs: ['role', 'constraints', 'examples'],
      outputs: ['system prompt text'],
      tasks: ['generate system prompt for ai agent', 'build chatgpt persona'],
    },
  },
  {
    id: 'midjourney-prompt-builder',
    name: 'Midjourney & Stable Diffusion Prompt Builder',
    description:
      'Visual builder for AI image generation prompts with styles, lighting, aspect ratios, and parameters',
    path: '/midjourney-prompt-builder',
    icon: Sparkles,
    category: 'ai',
    keywords: [
      'midjourney prompt',
      'stable diffusion prompt',
      'ai art generator',
      'aspect ratio --ar',
    ],
    search: {
      aliases: ['midjourney prompt builder', 'sd prompt maker', 'ai art prompt generator'],
      actions: ['build', 'generate', 'customize'],
      inputs: ['subject', 'art style', 'lighting', 'camera angle'],
      outputs: ['midjourney prompt string with parameters'],
      tasks: ['build midjourney prompt', 'create stable diffusion prompt'],
    },
  },
  {
    id: 'llm-tool-builder',
    name: 'OpenAI & Claude Function / Tool Spec Builder',
    description:
      'Convert JSON payloads and schemas into OpenAI Function Calling & Claude Tool Spec JSON',
    path: '/llm-tool-builder',
    icon: Wrench,
    category: 'ai',
    keywords: ['function calling spec', 'openai tools', 'claude tools', 'llm tool schema'],
    search: {
      aliases: ['openai tool spec generator', 'function calling json builder'],
      actions: ['generate', 'convert', 'build'],
      inputs: ['function name', 'json params'],
      outputs: ['openai tool JSON definition'],
      tasks: ['create openai function definition', 'generate claude tool spec'],
    },
  },
  {
    id: 'llm-response-cleaner',
    name: 'LLM Response Cleaner & Code Extractor',
    description:
      'Strip conversational fluff from ChatGPT/Claude responses and extract pure code blocks',
    path: '/llm-response-cleaner',
    icon: Scissors,
    category: 'ai',
    keywords: ['llm response cleaner', 'extract code from chatgpt', 'clean ai output'],
    search: {
      aliases: ['chatgpt response cleaner', 'ai code extractor'],
      actions: ['clean', 'extract', 'strip'],
      inputs: ['raw ai response text'],
      outputs: ['pure code block', 'sanitized markdown'],
      tasks: ['clean chatgpt output', 'extract code from claude response'],
    },
  },
  {
    id: 'prompt-enhancer',
    name: 'AI Prompt Enhancer & Quality Optimizer',
    description:
      'Transform basic text prompts into structured, high-quality prompts using proven frameworks',
    path: '/prompt-enhancer',
    icon: Sparkles,
    category: 'ai',
    keywords: [
      'prompt enhancer',
      'prompt optimizer',
      'prompt engineering',
      'better chatgpt prompts',
    ],
    search: {
      aliases: ['prompt improver', 'ai prompt optimizer', 'prompt quality enhancer'],
      actions: ['enhance', 'optimize', 'improve'],
      inputs: ['basic prompt text'],
      outputs: ['structured enhanced prompt'],
      tasks: ['improve chatgpt prompt quality', 'enhance prompt with context'],
    },
  },
  {
    id: 'openapi-to-postman',
    name: 'OpenAPI & Swagger to Postman Collection Generator',
    description:
      'Convert OpenAPI 3.0/3.1 and Swagger JSON/YAML into Postman Collections and Environment files with folder grouping by tags',
    path: '/openapi-to-postman',
    icon: Network,
    category: 'development',
    keywords: [
      'swagger to postman',
      'openapi to postman',
      'postman collection generator',
      'swagger converter',
      'postman environment',
    ],
    search: {
      aliases: ['swagger 2 postman', 'openapi postman converter', 'api spec to postman'],
      actions: ['convert', 'generate', 'export'],
      inputs: ['swagger json', 'openapi yaml'],
      outputs: ['postman collection json', 'postman environment json'],
      tasks: ['convert swagger to postman collection', 'export openapi to postman'],
    },
  },
  {
    id: 'ai-compatibility-checker',
    name: 'Site AI Compatibility & GEO Scanner',
    description:
      'Assess AI crawler directives, validate llms.txt structure, and check supplied HTML for Schema.org JSON-LD and semantic markup',
    path: '/ai-compatibility-checker',
    icon: Sparkles,
    category: 'ai',
    keywords: [
      'ai compatibility',
      'geo scanner',
      'llms.txt',
      'chatgpt search seo',
      'perplexity optimization',
      'gptbot',
      'claudebot',
      'ai seo audit',
    ],
    search: {
      aliases: ['geo audit', 'llms txt generator', 'ai search readiness', 'ai bot checker'],
      actions: ['audit', 'scan', 'generate', 'validate'],
      inputs: ['domain', 'robots.txt', 'llms.txt', 'html'],
      outputs: ['ai readiness score', 'llms.txt', 'geo recommendations'],
      tasks: [
        'audit site for chatgpt search',
        'generate llms.txt file',
        'check if gptbot is blocked',
      ],
    },
  },
  {
    id: 'internet-stability-test',
    name: 'Internet Stability & Load-Impact Test',
    description:
      'Estimate HTTP response-time variation, failed probe requests, and load impact from your browser',
    path: '/internet-stability-test',
    icon: Activity,
    category: 'network',
    keywords: [
      'internet stability',
      'bufferbloat test',
      'jitter test',
      'http latency test',
      'gaming lag test',
      'voip quality test',
    ],
    search: {
      aliases: ['browser latency tester', 'response-time jitter test', 'load impact checker'],
      actions: ['test', 'measure', 'benchmark'],
      inputs: ['internet connection'],
      outputs: [
        'browser HTTP response time',
        'response-time variation',
        'probe failure rate',
        'idle-to-load response-time difference',
      ],
      tasks: [
        'test internet stability',
        'compare browser response times under load',
        'check failed HTTP probe rate',
      ],
    },
  },
  {
    id: 'http-headers-inspector',
    name: 'HTTP Response Header Inspector',
    description:
      'Inspect browser-readable HTTP responses and exposed headers; cross-origin and redirect visibility depends on browser CORS rules',
    path: '/http-headers-inspector',
    icon: Globe,
    category: 'network',
    keywords: [
      'http headers',
      'final redirect response',
      'http status checker',
      'hsts check',
      'csp validator',
      'response headers',
    ],
    search: {
      aliases: [
        'redirect trace',
        'header inspector',
        '301 redirect checker',
        'security headers test',
      ],
      actions: ['inspect', 'trace', 'check', 'audit'],
      inputs: ['url', 'domain'],
      outputs: [
        'final HTTP status',
        'CORS-exposed response headers',
        'visible security header checks',
      ],
      tasks: [
        'inspect browser-readable http response headers',
        'check security headers exposed to the browser',
        'copy a curl command for terminal-based redirect inspection',
      ],
    },
  },
  {
    id: 'pixel-art-converter',
    name: 'Image to Pixel Art & Retro Palette Studio',
    description:
      'Convert images to 8-bit & 16-bit retro pixel art with Game Boy, NES, and PICO-8 palettes, dithering, and SVG/CSS export',
    path: '/pixel-art-converter',
    icon: Grid2x2,
    category: 'image',
    keywords: [
      'pixel art',
      'image to pixel art',
      'pixel art maker',
      'retro 8-bit converter',
      'gameboy palette',
      'pico-8',
      'nes color palette',
      'dithering tool',
      'sprite generator',
    ],
    search: {
      aliases: [
        'pixel art generator',
        'image pixelator',
        '8 bit photo converter',
        'dither image online',
        'sprite maker',
      ],
      actions: ['convert', 'draw', 'pixelate', 'dither', 'export'],
      inputs: ['image', 'photo', 'sprite', 'png', 'jpeg'],
      outputs: ['pixel art png', 'svg', 'css box shadow', 'palette'],
      tasks: [
        'convert image to pixel art',
        'apply gameboy retro palette',
        'export pixel art as svg or css',
        'draw custom 8 bit sprite',
      ],
    },
  },
  {
    id: 'pdf-tools',
    name: 'PDF Tools & Page Manager',
    description:
      '100% private client-side PDF tools: Merge multiple PDFs, split & extract custom page ranges, rotate pages, and convert PNG/JPG images to PDF',
    path: '/pdf-tools',
    icon: Layers,
    category: 'image',
    keywords: [
      'pdf merge',
      'merge pdf',
      'split pdf',
      'pdf combiner',
      'extract pdf pages',
      'rotate pdf',
      'image to pdf',
      'png to pdf',
      'jpg to pdf',
      'client side pdf tools',
      'free pdf joiner',
    ],
    search: {
      aliases: [
        'merge pdf online',
        'combine pdf files',
        'split pdf pages',
        'images to pdf converter',
        'rotate pdf document',
      ],
      actions: ['merge', 'split', 'rotate', 'convert', 'extract'],
      inputs: ['pdf', 'png', 'jpg', 'jpeg'],
      outputs: ['merged pdf', 'split pdf', 'rotated pdf'],
      tasks: [
        'merge multiple pdfs into one document',
        'split pdf by page range',
        'rotate sideways pdf pages',
        'convert photos and images to pdf',
      ],
    },
  },

  // Hardware & Device Tests
  {
    id: 'dead-pixel-test',
    name: 'Dead Pixel & Monitor Test',
    description:
      'Full-screen solid colors, gradients, and checkerboard patterns to spot dead pixels, stuck pixels, and backlight bleed',
    path: '/dead-pixel-test',
    icon: Monitor,
    category: 'hardware',
    keywords: [
      'dead pixel test',
      'stuck pixel test',
      'monitor test',
      'screen test',
      'olu pixel testi',
      'ekran testi',
      'backlight bleed test',
      'lcd test',
    ],
    search: {
      aliases: ['pixel checker', 'monitor checker', 'stuck pixel finder', 'ekran piksel testi'],
      actions: ['test', 'check', 'find', 'inspect'],
      inputs: ['monitor', 'laptop screen', 'tv screen'],
      outputs: ['dead pixel', 'stuck pixel', 'backlight bleed', 'uniformity'],
      tasks: [
        'check monitor for dead pixels',
        'test new laptop screen for stuck pixels',
        'olu pixel var mi kontrol et',
      ],
    },
  },
  {
    id: 'keyboard-tester',
    name: 'Keyboard Tester',
    description:
      'Press any key to see it highlight on a full on-screen keyboard, log key codes, and test for stuck keys or rollover issues',
    path: '/keyboard-tester',
    icon: Keyboard,
    category: 'hardware',
    keywords: [
      'keyboard tester',
      'key tester',
      'stuck key test',
      'keyboard rollover test',
      'nkro test',
      'klavye testi',
      'test keyboard online',
    ],
    search: {
      aliases: ['key test', 'keyboard checker', 'ghosting test', 'klavye tus testi'],
      actions: ['test', 'press', 'check', 'log'],
      inputs: ['keyboard', 'keypress'],
      outputs: ['key code', 'key name', 'stuck key', 'rollover support'],
      tasks: [
        'test if all keyboard keys work',
        'check for keyboard ghosting',
        'klavye tuslarini test et',
      ],
    },
  },
  {
    id: 'mouse-tester',
    name: 'Mouse Tester',
    description:
      'Test left, right, and middle click, double-click misfires, scroll wheel, and mouse movement in real-time',
    path: '/mouse-tester',
    icon: MousePointer2,
    category: 'hardware',
    keywords: [
      'mouse tester',
      'click test',
      'double click test',
      'scroll wheel test',
      'fare testi',
      'mouse button test',
    ],
    search: {
      aliases: ['click checker', 'mouse checker', 'fare tik testi'],
      actions: ['test', 'click', 'scroll', 'check'],
      inputs: ['mouse', 'left click', 'right click', 'middle click', 'scroll wheel'],
      outputs: ['click count', 'double click misfire', 'scroll delta'],
      tasks: [
        'test mouse buttons for double click issue',
        'check mouse scroll wheel',
        'fare tiklama sorunu test et',
      ],
    },
  },
  {
    id: 'touchscreen-tester',
    name: 'Touchscreen Test',
    description:
      'Multi-touch point map for phones, tablets, and touchscreen laptops to find dead zones and check touch accuracy',
    path: '/touchscreen-tester',
    icon: Hand,
    category: 'hardware',
    keywords: [
      'touchscreen test',
      'touch test',
      'multi touch test',
      'dokunmatik ekran testi',
      'phone screen test',
      'tablet touch test',
    ],
    search: {
      aliases: ['touch checker', 'dead zone test', 'dokunmatik test'],
      actions: ['test', 'touch', 'check'],
      inputs: ['touchscreen', 'finger touch'],
      outputs: ['touch point', 'dead zone', 'touch radius'],
      tasks: [
        'test phone touchscreen for dead spots',
        'check tablet multi touch',
        'dokunmatik ekrani test et',
      ],
    },
  },
  {
    id: 'gamepad-tester',
    name: 'Gamepad Tester',
    description:
      'Connect a controller and see every button press, trigger, and analog stick movement live, with stick drift detection',
    path: '/gamepad-tester',
    icon: Gamepad2,
    category: 'hardware',
    keywords: [
      'gamepad tester',
      'controller test',
      'joystick test',
      'stick drift test',
      'kumanda testi',
      'xbox controller test',
      'ps5 controller test',
    ],
    search: {
      aliases: ['controller checker', 'joystick checker', 'kumanda test'],
      actions: ['test', 'press', 'check', 'calibrate'],
      inputs: ['gamepad', 'controller', 'joystick'],
      outputs: ['button state', 'axis position', 'stick drift'],
      tasks: [
        'test controller buttons online',
        'check for analog stick drift',
        'kumandayi test et',
      ],
    },
  },
  {
    id: 'speaker-test',
    name: 'Speaker & Audio Channel Test',
    description:
      'Play tones through left and right channels and sweep frequencies to test speakers, headphones, and audio balance',
    path: '/speaker-test',
    icon: Volume2,
    category: 'hardware',
    keywords: [
      'speaker test',
      'audio test',
      'left right channel test',
      'headphone test',
      'hoparlor testi',
      'frequency sweep test',
    ],
    search: {
      aliases: ['audio channel checker', 'stereo test', 'kulaklik testi'],
      actions: ['test', 'play', 'check'],
      inputs: ['speaker', 'headphones', 'audio output'],
      outputs: ['left channel', 'right channel', 'tone frequency'],
      tasks: [
        'test left and right speaker channels',
        'check headphones for balance issues',
        'hoparlorleri test et',
      ],
    },
  },
  {
    id: 'microphone-test',
    name: 'Microphone Test',
    description:
      'Check your microphone input level in real-time with a live volume meter, entirely in your browser with no recording or upload',
    path: '/microphone-test',
    icon: Mic,
    category: 'hardware',
    keywords: [
      'microphone test',
      'mic test',
      'test my mic online',
      'mikrofon testi',
      'mic level meter',
    ],
    search: {
      aliases: ['mic checker', 'microphone level test', 'mikrofon test'],
      actions: ['test', 'check', 'measure'],
      inputs: ['microphone', 'audio input'],
      outputs: ['input level', 'volume meter'],
      tasks: [
        'test if my microphone works',
        'check mic input level before a call',
        'mikrofonumu test et',
      ],
    },
  },
  {
    id: 'webcam-test',
    name: 'Webcam Test',
    description:
      'Preview your webcam locally in the browser and check resolution and frame rate, with no video ever leaving your device',
    path: '/webcam-test',
    icon: Camera,
    category: 'hardware',
    keywords: [
      'webcam test',
      'camera test',
      'test my webcam online',
      'kamera testi',
      'webcam checker',
    ],
    search: {
      aliases: ['camera checker', 'webcam preview', 'kamera test'],
      actions: ['test', 'preview', 'check'],
      inputs: ['webcam', 'camera'],
      outputs: ['video preview', 'resolution', 'frame rate'],
      tasks: ['test if my webcam works', 'check webcam before a video call', 'kameramı test et'],
    },
  },
  {
    id: 'refresh-rate-test',
    name: 'Screen Refresh Rate Test',
    description:
      'Measures the actual refresh rate of your monitor in real-time and shows a moving bar to visually check for judder',
    path: '/refresh-rate-test',
    icon: Gauge,
    category: 'hardware',
    keywords: [
      'refresh rate test',
      'monitor hz test',
      '60hz 144hz test',
      'ekran yenileme hizi testi',
      'display hz checker',
    ],
    search: {
      aliases: ['hz checker', 'monitor frequency test', 'ekran hz testi'],
      actions: ['test', 'measure', 'check'],
      inputs: ['monitor', 'display'],
      outputs: ['refresh rate hz', 'frame timing'],
      tasks: [
        'check monitor refresh rate',
        'verify 144hz monitor is working',
        'ekranin hzsini test et',
      ],
    },
  },
  {
    id: 'sensor-test',
    name: 'Device Sensor Test',
    description:
      'Live readout of your phone or tablet accelerometer, gyroscope, and compass sensors, entirely in your browser',
    path: '/sensor-test',
    icon: Compass,
    category: 'hardware',
    keywords: [
      'sensor test',
      'gyroscope test',
      'accelerometer test',
      'compass test',
      'sensor testi',
      'phone sensor checker',
    ],
    search: {
      aliases: ['gyroscope checker', 'motion sensor test', 'sensor test telefon'],
      actions: ['test', 'check', 'measure'],
      inputs: ['gyroscope', 'accelerometer', 'compass', 'phone motion'],
      outputs: ['orientation degrees', 'acceleration', 'compass heading'],
      tasks: [
        'test phone gyroscope',
        'check accelerometer and compass sensors',
        'telefon sensorlerini test et',
      ],
    },
  },
  {
    id: 'battery-status',
    name: 'Battery Status',
    description:
      'Shows the current battery charge level, charging state, and estimated time remaining for this device where the browser supports it',
    path: '/battery-status',
    icon: BatteryFull,
    category: 'hardware',
    keywords: [
      'battery status',
      'battery health check',
      'battery level checker',
      'batarya durumu',
      'check laptop battery',
    ],
    search: {
      aliases: ['battery checker', 'charge level test', 'batarya test'],
      actions: ['check', 'view', 'monitor'],
      inputs: ['device battery'],
      outputs: ['charge level', 'charging state', 'time remaining'],
      tasks: [
        'check laptop battery percentage',
        'see if my device is charging',
        'batarya seviyesini kontrol et',
      ],
    },
  },
  {
    id: 'cpu-benchmark',
    name: 'CPU Benchmark',
    description:
      'Scores single-core and multi-core CPU throughput with a real Web Worker workload, entirely in your browser',
    path: '/cpu-benchmark',
    icon: Cpu,
    category: 'hardware',
    keywords: [
      'cpu benchmark',
      'cpu speed test',
      'processor benchmark',
      'islemci testi',
      'multi core test',
      'online cpu test',
    ],
    search: {
      aliases: ['cpu test', 'processor test', 'islemci hiz testi'],
      actions: ['benchmark', 'test', 'measure'],
      inputs: ['cpu', 'processor'],
      outputs: ['single core score', 'multi core score', 'core count'],
      tasks: [
        'benchmark my cpu online',
        'test single core and multi core performance',
        'islemcimi test et',
      ],
    },
  },
  {
    id: 'memory-benchmark',
    name: 'Memory Info & Benchmark',
    description:
      'Shows what the browser exposes about system memory and measures real allocation throughput, entirely in your browser',
    path: '/memory-benchmark',
    icon: MemoryStick,
    category: 'hardware',
    keywords: [
      'ram test',
      'memory benchmark',
      'ram benchmark',
      'ram testi',
      'device memory checker',
      'js heap usage',
    ],
    search: {
      aliases: ['ram checker', 'memory test', 'bellek testi'],
      actions: ['benchmark', 'test', 'measure', 'check'],
      inputs: ['ram', 'memory'],
      outputs: ['device memory', 'heap usage', 'allocation throughput'],
      tasks: ['test ram allocation speed', 'check browser memory usage', 'bellegimi test et'],
    },
  },
  {
    id: 'gpu-benchmark',
    name: 'GPU Benchmark',
    description:
      'Renders an animated WebGL stress test and measures sustained frame rate as a relative GPU throughput score',
    path: '/gpu-benchmark',
    icon: MonitorCog,
    category: 'hardware',
    keywords: [
      'gpu benchmark',
      'graphics card test',
      'webgl benchmark',
      'gpu testi',
      'ekran karti testi',
      'fps benchmark',
    ],
    search: {
      aliases: ['gpu test', 'graphics benchmark', 'ekran karti test'],
      actions: ['benchmark', 'test', 'measure'],
      inputs: ['gpu', 'graphics card'],
      outputs: ['renderer name', 'sustained fps', 'gpu score'],
      tasks: ['benchmark my graphics card online', 'test gpu frame rate', 'ekran kartimi test et'],
    },
  },
  {
    id: 'disk-speed-test',
    name: 'Storage Speed Test',
    description:
      'Measures real read and write throughput to the browser sandboxed storage and shows storage quota usage',
    path: '/disk-speed-test',
    icon: HardDrive,
    category: 'hardware',
    keywords: [
      'disk speed test',
      'storage speed test',
      'ssd test',
      'disk testi',
      'read write speed',
      'storage quota',
    ],
    search: {
      aliases: ['disk test', 'ssd benchmark', 'depolama testi'],
      actions: ['test', 'measure', 'benchmark'],
      inputs: ['browser storage'],
      outputs: ['write speed', 'read speed', 'storage quota'],
      tasks: [
        'test browser storage read and write speed',
        'check storage quota used by this site',
        'depolama hizini test et',
      ],
    },
  },
];
